/**
 * Montage-Picker + F-Gas-Gate
 *
 * Der Picker ersetzt Horizons Variantenauswahl nicht, er bedient sie. Horizons
 * <variant-picker> bleibt im DOM (visuell versteckt) und macht weiter alles,
 * was er ohnehin gut macht: Preis, Verfügbarkeit, Varianten-ID im Formular,
 * URL-Zustand. Wir schicken ihm Klicks und lesen seinen Zustand zurück.
 *
 * Das Gate ist eine Rechtspflicht: Split-Geräte mit R32 sind nicht hermetisch
 * geschlossen. Nach (EU) 2024/573 dürfen sie nur abgegeben werden, wenn
 * nachgewiesen ist, dass ein zertifizierter Betrieb installiert.
 *
 * ARCHITEKTUR DER SPERRE — und warum sie so und nicht anders ist
 *
 * Ein disabled-Attribut allein reicht nicht: Horizon rendert die Produktsektion
 * bei jedem Variantenwechsel serverseitig neu und morpht sie ins DOM. Dabei
 * verschwindet das Attribut wieder, und der Button sieht gesperrt aus, ohne es
 * zu sein.
 *
 * Ein MutationObserver, der das Attribut nachsetzt, war der zweite Versuch —
 * und der falsche: Er hat sich mit Horizons Morphing hochgeschaukelt, bis der
 * Renderer stand. Wer unser Element wieder einfügt, während das Morphing es
 * entfernt, baut eine Endlosschleife.
 *
 * Deshalb jetzt: Die Wahrheit liegt in einer Modulvariablen, nicht im DOM. Ein
 * Guard in der Capture-Phase auf click und submit greift, bevor Horizons eigene
 * Handler laufen, und ist von Re-Renders vollständig unabhängig. Das
 * disabled-Attribut ist nur noch Optik und wird nach einem Wechsel ein paarmal
 * zeitversetzt nachgezogen.
 *
 * Grenze, die offen bleibt: Das ist Frontend. Wer den Warenkorb über die
 * Cart-API befüllt, umgeht es. Eine harte Sperre braucht eine Shopify Function
 * vom Typ "Cart and Checkout Validation". Siehe HANDOVER.
 */

const PROPERTY_NAME = 'properties[Einbaubestätigung]';

const PROPERTY_TEXT = {
  montage: 'Fachmontage durch Gletscherluft, zertifizierter Kältefachbetrieb',
  confirmed:
    'Kunde bestätigt: Installation und Inbetriebnahme durch einen nach (EU) 2024/573 zertifizierten Kältefachbetrieb',
};

const BUY_SELECTOR = 'button[type="submit"][name="add"]';
const EXPRESS_SELECTOR = '.shopify-payment-button, shopify-accelerated-checkout';

/* Quelle der Wahrheit. Nur der Picker schreibt, die Guards lesen. */
let locked = false;
let propertyValue = null;

function ensureProperty(form) {
  if (!form) return;
  let input = form.querySelector(`input[name="${PROPERTY_NAME}"]`);

  if (!propertyValue) {
    if (input) input.remove();
    return;
  }

  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = PROPERTY_NAME;
    form.appendChild(input);
  }
  if (input.value !== propertyValue) input.value = propertyValue;
}

function ensureAllProperties() {
  document.querySelectorAll('form[action*="/cart/add"]').forEach(ensureProperty);
}

function focusGate() {
  const box = document.querySelector('[data-gl-confirm]');
  if (box) box.focus();
}

function block(event) {
  event.preventDefault();
  event.stopPropagation();
  if (event.stopImmediatePropagation) event.stopImmediatePropagation();
  focusGate();
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const hit = target.closest(BUY_SELECTOR + ', ' + EXPRESS_SELECTOR);
    if (!hit) return;
    if (locked) {
      block(event);
      return;
    }
    // Das Morphing kann unser Hidden-Input entfernt haben. Kurz vor dem Kauf
    // ist der letztmögliche Zeitpunkt, es wieder hinzuschreiben.
    ensureProperty(hit.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]'));
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;
    if (locked) {
      block(event);
      return;
    }
    ensureProperty(form);
  },
  true
);

class MontagePicker extends HTMLElement {
  connectedCallback() {
    this.optionName = this.dataset.optionName || 'Montage';
    this.gateValue = this.dataset.gateValue || '';

    this.cards = Array.from(this.querySelectorAll('[data-gl-card]'));
    this.gate = this.querySelector('[data-gl-gate]');
    this.confirmBox = this.querySelector('[data-gl-confirm]');

    if (!this.nativeInputs().length && !this.nativeSelect()) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    document.documentElement.classList.add('gl-native-hidden');

    this.onCardChange = this.onCardChange.bind(this);
    this.onConfirmChange = this.onConfirmChange.bind(this);
    this.onNativeChange = this.onNativeChange.bind(this);

    this.cards.forEach((card) => card.addEventListener('change', this.onCardChange));
    if (this.confirmBox) this.confirmBox.addEventListener('change', this.onConfirmChange);
    document.addEventListener('change', this.onNativeChange);

    this.syncFromNative();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onNativeChange);
  }

  scope() {
    return this.closest('.shopify-section') || document.body;
  }

  nativeInputs() {
    return Array.from(
      this.scope().querySelectorAll(`input[type="radio"][data-option-name="${CSS.escape(this.optionName)}"]`)
    );
  }

  nativeSelect() {
    return this.scope().querySelector(`select[name="options[${this.optionName}]"]`);
  }

  nativeValue() {
    const select = this.nativeSelect();
    if (select) return select.value;
    const checked = this.nativeInputs().find((input) => input.checked);
    return checked ? checked.value : '';
  }

  selectNative(value) {
    const select = this.nativeSelect();
    if (select) {
      if (select.value === value) return;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const target = this.nativeInputs().find((input) => input.value === value);
    if (!target || target.checked) return;
    target.click();
  }

  onCardChange(event) {
    this.selectNative(event.target.value);
    this.update(event.target.value);
  }

  onConfirmChange() {
    this.update(this.nativeValue() || this.checkedCardValue());
  }

  onNativeChange(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const isRadio = target.matches(`input[data-option-name="${CSS.escape(this.optionName)}"]`);
    const isSelect = target.matches(`select[name="options[${this.optionName}]"]`);
    if (!isRadio && !isSelect) return;
    this.syncFromNative();
  }

  syncFromNative() {
    const value = this.nativeValue() || this.checkedCardValue();
    this.cards.forEach((card) => {
      card.checked = card.value === value;
    });
    this.update(value);
  }

  checkedCardValue() {
    const checked = this.cards.find((card) => card.checked);
    return checked ? checked.value : '';
  }

  update(value) {
    const needsGate = Boolean(this.gateValue) && value === this.gateValue;

    if (this.gate) {
      this.gate.hidden = !needsGate;
      if (!needsGate && this.confirmBox) this.confirmBox.checked = false;
    }

    const confirmed = Boolean(this.confirmBox && this.confirmBox.checked);

    locked = needsGate && !confirmed;
    propertyValue = locked ? null : needsGate ? PROPERTY_TEXT.confirmed : PROPERTY_TEXT.montage;

    this.paint();

    // Horizon rendert nach einem Variantenwechsel asynchron nach. Statt jede
    // Mutation zu beobachten — was sich mit dem Morphing aufgeschaukelt hat —
    // ziehen wir die Optik ein paarmal zeitversetzt nach. Die eigentliche
    // Sperre hängt ohnehin nicht daran.
    [120, 500, 1200, 2500].forEach((delay) => setTimeout(() => this.paint(), delay));
  }

  /** Nur Optik. Muss idempotent sein und darf nichts erzwingen. */
  paint() {
    document.body.classList.toggle('gl-buy-locked', locked);

    document.querySelectorAll(BUY_SELECTOR).forEach((button) => {
      if (locked) {
        if (!button.disabled) button.setAttribute('disabled', '');
        button.dataset.glLocked = 'true';
      } else if (button.dataset.glLocked === 'true') {
        // Nur das eigene Attribut zurücknehmen. Ein ausverkauftes Produkt ist
        // auch ohne uns disabled und muss disabled bleiben.
        button.removeAttribute('disabled');
        delete button.dataset.glLocked;
      }
    });

    const note = this.scope().querySelector('[data-gl-lock-note]');
    if (note) note.hidden = !locked;

    ensureAllProperties();
  }
}

if (!customElements.get('montage-picker')) {
  customElements.define('montage-picker', MontagePicker);
}
